import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface TradingModeContextType {
  tradingMode: 'paper' | 'live';
  toggleMode: () => Promise<void>;
  kalshiConnected: boolean;
  isToggling: boolean;
  /** API query param value: 'paper' or 'live' */
  modeParam: 'paper' | 'live';
}

const TradingModeContext = createContext<TradingModeContextType>({
  tradingMode: 'paper',
  toggleMode: async () => {},
  kalshiConnected: false,
  isToggling: false,
  modeParam: 'paper',
});

export const useTradingMode = () => useContext(TradingModeContext);

export const TradingModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refreshUser } = useAuth();

  const tradingMode: 'paper' | 'live' = user?.trading_mode || 'paper';
  const kalshiConnected = user?.kalshi_connected || user?.kalshi_api_connected || false;
  const [isToggling, setIsToggling] = useState(false);

  const toggleMode = useCallback(async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      const newMode = tradingMode === 'paper' ? 'live' : 'paper';
      await api.post('/api/v1/autonomous/trading-mode', { mode: newMode });
      await refreshUser();
    } catch (err) {
      console.error('Failed to toggle trading mode:', err);
    } finally {
      setIsToggling(false);
    }
  }, [tradingMode, isToggling, refreshUser]);

  const modeParam = tradingMode;

  return (
    <TradingModeContext.Provider value={{ tradingMode, toggleMode, kalshiConnected, isToggling, modeParam }}>
      {children}
    </TradingModeContext.Provider>
  );
};
